/**
 * English UI catalogue of the new-chart wizard (namespace 'wizard'; W2-14 onboarding-wizard, DESIGN.md §4.3, §5.1).
 * zh-CN lives in src/i18n/zh-CN/wizard.ts (W3-09).
 *
 * Keys are never written into a text (the user's keymap decides them): remindBeat takes {key}, the label of the key
 * bound to the Song & Timing workspace now, or 'none'.
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('wizard', {
  // ---- dialog ----
  title: 'New chart',
  stepsLabel: 'Steps of the new chart',
  stepState:
    '{n}. {title} ({state, select, done {done} skipped {skipped for now} current {current step} other {not done yet}})',
  back: 'Back',
  firstStep: 'This is the first step.',
  nextBlocked: 'Complete this step first, or skip it for now.',
  nextBlockedSong: 'Choose a song that Phira can play first.',
  next: 'Next: {step}',
  create: 'Create chart',
  creating: 'Creating your chart...',
  skip: 'Skip for now',
  cancel: 'Cancel',
  discardTitle: 'Discard this new chart?',
  discardBody: 'The song and the details you entered are not saved yet.',
  discardConfirm: 'Discard',
  errorCreate: 'The chart could not be created: {detail}',
  errorInvalid: 'The new chart did not pass the document checks ({detail}). Please report this.',
  errorNoSong: 'Choose a song first.',
  errorPreview: 'The song-select preview must lie inside the song.',
  persistRefused: 'Your browser did not promise to keep your charts. Download a backup from the Library now and then.',
  tourOfferTitle: 'Take the 2-minute tour?',
  tourOfferBody: 'Eight short steps show the line, playback, notes, motion and publishing in your new chart.',
  tourOfferStart: 'Start the tour',
  untitled: 'Untitled',

  // ---- step titles, command, setting ----
  stepSong: 'Song',
  stepPicture: 'Picture',
  stepDetails: 'Details',
  stepStart: 'Start',
  cmdNew: 'New chart',
  settingCharter: 'Charter name for new charts',
  settingCharterHelp: 'The new-chart wizard fills it in and remembers what you typed last.',

  // ---- song ----
  songDrop: 'Drop the song here, or',
  chooseFile: 'Choose file',
  songAccepted: 'MP3, OGG, WAV or FLAC. M4A and Opus are converted to WAV.',
  songLoading: 'Reading the song...',
  songFacts: '{duration} · {codec} · {rate} kHz · {channels, plural, one {mono} =2 {stereo} other {# channels}}',
  codecMp3: 'MP3',
  codecOgg: 'OGG Vorbis',
  codecWav: 'WAV',
  codecFlac: 'FLAC',
  codecAac: 'AAC',
  codecM4a: 'M4A (AAC in MP4)',
  codecOpus: 'Opus',
  codecOther: '{codec}',
  verdictOk: 'Phira can play this.',
  verdictMp3: 'Phira can play this. OGG or WAV keeps timing most exact across players (MP3 decoder delay).',
  verdictNotForRpe: 'Phira can play this. RPE cannot open it.',
  verdictConvert: 'Phira cannot play this format.',
  convertToWav: 'Convert to WAV',
  convertHint: 'A bigger file that always works.',
  convertForRpe: 'Convert to WAV for RPE',
  convertForRpeHint: 'Optional: only needed if you also open the chart in RPE.',
  converting: 'Converting to WAV...',
  converted: 'Converted to WAV ({size} MB).',
  convertFailed: 'The conversion failed: {detail}',
  chooseAnother: 'Choose another file',
  removeSong: 'Remove song',
  play: 'Play',
  stop: 'Stop',
  playUnavailable: 'Sound is not available in this browser.',
  waveformLabel: 'Waveform of {name}',
  errNotAudio: '"{name}" is not an audio file.',
  errDecode: 'Your browser could not decode "{name}". Try an MP3, OGG or WAV file.',
  errTooShort: 'The song is too short: it must last at least {min} seconds.',
  errRead: 'The file could not be read: {detail}',
  detecting: 'Detecting the tempo in the background...',
  detected: 'Tempo found: about {bpm} BPM. The Beat step lets you check it.',
  detectedNone: 'No clear tempo found. The Beat step lets you tap it.',
  detectFailed: 'Tempo detection is not available right now. The Beat step lets you tap the tempo.',

  // ---- picture ----
  picDrop: 'Drop a picture here, or',
  picAccepted: 'PNG or JPG (works everywhere), WebP, BMP or GIF.',
  picLoading: 'Reading the picture...',
  picFacts: '{w} x {h} px · {codec}',
  previewGame: 'In the game',
  previewGameHint: 'Blurred and dimmed, as Phira draws it behind the chart.',
  previewSelect: 'Song select',
  previewSelectHint: 'Cropped to 16:9.',
  previewBusy: 'Drawing the preview...',
  previewUnavailable: 'The preview is not available right now.',
  picLarge: 'This picture is {w} x {h} px. Phira loads it, but a copy of at most 4096 px loads faster.',
  picDownscale: 'Use a 4096 px copy',
  picDownscaling: 'Making a smaller copy...',
  picUnsupported: 'Phira cannot show this image format.',
  picConvert: 'Convert to PNG',
  picConverting: 'Converting to PNG...',
  picNotForRpe: 'RPE opens only PNG and JPG pictures.',
  picRemove: 'Remove picture',
  picPlaceholder:
    'No picture yet: a gradient made from the title is used, and Problems reminds you. You can choose a picture any time in Assets.',
  picNotImage: '"{name}" is not a picture.',
  picError: 'The picture could not be read: {detail}',

  // ---- details ----
  fieldTitle: 'Title',
  fieldArtist: 'Artist',
  fieldIllustrator: 'Illustrator',
  fieldCharter: 'Charter',
  charterRemembered: 'Remembered for your next chart.',
  prefilled: 'Filled in from the song file.',
  titleRequired: 'Enter a title, or skip this step for now.',
  difficultyLabel: 'Difficulty',
  labelCustom: 'Custom',
  customLabel: 'Custom label',
  levelNumber: 'Level',
  levelPreview: 'Shown as "{level}"',
  previewTitle: 'Song-select preview',
  previewHint: 'Players hear this part of the song when they pick the chart.',
  previewStart: 'Starts at',
  previewEnd: 'Ends at',
  unitSeconds: 's',
  previewPlay: 'Play preview',
  previewNegative: 'The preview cannot start before the song.',
  previewOrder: 'The preview must end after it starts.',
  previewBeyond: 'The preview must end at least 0.5 s before the song ends (at {max} s at the latest).',
  previewNotFinite: 'Enter a number of seconds.',
  previewWaveformLabel: 'Waveform of the song with the preview range; click to move the preview there',
  noSongYet: 'Choose a song first: the preview range depends on its length.',

  // ---- start ----
  startIntro: 'How should the chart begin?',
  layoutsLabel: 'Starting layout',
  layoutOneLine: 'One line',
  layoutOneLineBody: 'The classic start: one line in the lower third.',
  layoutTwoLines: 'Two lines',
  layoutTwoLinesBody: 'Two lines, one above the other.',
  layoutEmpty: 'Empty',
  layoutEmptyBody: 'No lines yet. Add them in the Lines panel.',
  layoutRpe24: 'RPE-style 24 lines',
  layoutRpe24Body: 'Line 0 visible, lines 1 to 23 hidden: the usual RPE template.',
  lineMain: 'Main',
  lineSecond: 'Second',
  lineNumbered: 'Line {n}',
  summaryTitle: 'Your new chart',
  summarySong: 'Song: {name} ({duration})',
  summaryDetails: '{title} · {level}',
  summaryTiming: 'Tempo {bpm} BPM, beat 0 at {offset} s',
  summaryTimingUnconfirmed: 'Tempo {bpm} BPM, beat 0 at {offset} s (not confirmed yet)',
  remindTitle: 'Problems will remind you about:',
  remindBeat: 'Timing not confirmed: set the tempo and beat 0 in Song & Timing{key, select, none {} other { ({key})}}.',
  remindPicture: 'The illustration is a generated placeholder.',
  remindDetails: 'Song details you skipped (title, artist, illustrator, charter).',
  persistNote:
    'Your browser may ask whether this site may keep data: allow it, so your charts are never cleaned up automatically.',
});

export default catalogOf(m);
